import checkCircleFilledTokens from '@amzn/meridian-tokens/base/icon/check-circle-filled';
import closeCircleTokens from '@amzn/meridian-tokens/base/icon/close-circle';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import FileInput from '@amzn/meridian/file-input';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import styled from '@emotion/styled';
import React, { FC, useState } from 'react';
import { DocumentAttachment } from '../../types';
import { truncateWithPopover } from '../../utils/textUtils';
import { useTranslation } from '../blocks';

const SHOW_STATUS_WAIT_MS = 1000;

enum UploadStatus {
    SUCCESS = 'success',
    FAILED = 'failed',
    UPLOADING = 'uploading',
}

interface FileWithStatus {
    file: File;
    status?: UploadStatus;
}

interface DocumentUploaderProps {
    description: string;
    acceptedFileFormats: string;
    uploadButtonLabel: string;
    onUpload: (file: File) => Promise<DocumentAttachment>;
    maxFileSizeMB?: number;
    onDocumentUploaded?: (document: DocumentAttachment) => void; // notify parent of document upload completed
}

const SuccessIcon = styled(Icon)`
    color: #34934a;
`;

const FailureIcon = styled(Icon)`
    color: #c21f39;
`;

export const DocumentUploader: FC<DocumentUploaderProps> = ({
    description,
    acceptedFileFormats,
    uploadButtonLabel,
    onUpload,
    maxFileSizeMB = 10,
    onDocumentUploaded,
}) => {
    const [files, setFiles] = useState<FileWithStatus[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [oversizedFiles, setOversizedFiles] = useState<string[]>([]);
    const { t } = useTranslation('claimDocuments');
    const { t: tCommon } = useTranslation('common');

    const handleFileSelection = (newFiles: File[]) => {
        const MAX_FILE_SIZE = maxFileSizeMB * 1024 * 1024;
        const oversized: string[] = [];

        setFiles((prev) => {
            const nonFailedFiles = prev.filter((f) => f.status !== UploadStatus.FAILED);

            const validFiles = newFiles.filter((file) => {
                if (file.size > MAX_FILE_SIZE) {
                    oversized.push(file.name);
                    return false;
                }
                return !nonFailedFiles.some((f) => f.file.name === file.name);
            });

            const fileItems = validFiles.map((file): FileWithStatus => ({ file }));
            return [...nonFailedFiles, ...fileItems];
        });
        setOversizedFiles(oversized);
    };

    const removePendingFile = (fileName: string) => {
        setFiles((prev) => prev.filter((f) => f.file.name !== fileName));
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
    };

    const uploadSingleFile = async (fileItem: FileWithStatus) => {
        setFiles((prev) =>
            prev.map((f) => (f.file.name === fileItem.file.name ? { ...f, status: UploadStatus.UPLOADING } : f))
        );

        try {
            const uploadedDoc = await onUpload(fileItem.file);

            setFiles((prev) =>
                prev.map((f) => (f.file.name === fileItem.file.name ? { ...f, status: UploadStatus.SUCCESS } : f))
            );

            // notify parent that document has been uploaded successfully
            if (onDocumentUploaded) {
                onDocumentUploaded(uploadedDoc);
            }

            setTimeout(() => {
                setFiles((prev) => prev.filter((f) => f.file.name !== fileItem.file.name));
            }, SHOW_STATUS_WAIT_MS);
        } catch (error) {
            setFiles((prev) =>
                prev.map((f) => (f.file.name === fileItem.file.name ? { ...f, status: UploadStatus.FAILED } : f))
            );
        }
    };

    const handleBatchUpload = async () => {
        const filesToUpload = files.filter((f) => !f.status || f.status === UploadStatus.FAILED);
        if (!filesToUpload.length) return;

        // Reset failed files status
        setFiles((prev) => prev.map((f) => (f.status === UploadStatus.FAILED ? { ...f, status: undefined } : f)));

        setIsUploading(true);

        await filesToUpload.reduce(async (promise, fileItem) => {
            await promise;
            return uploadSingleFile(fileItem);
        }, Promise.resolve());

        setIsUploading(false);
    };

    const failedFiles = files.filter((f) => f.status === UploadStatus.FAILED);

    const formatAcceptedFileFormats = (formats: string) => {
        return formats.replaceAll('.', ' ').toUpperCase();
    };

    return (
        <>
            <Text type={'b300'}>{description}</Text>
            <FileInput
                type={'multiple'}
                uploadButtonLabel={uploadButtonLabel}
                onFileAttached={handleFileSelection}
                accept={acceptedFileFormats}
                dropZoneLabel={t('document-uploader-drop-zone-label')}
            />
            <Text color={'secondary'} type={'b100'}>
                {t('document-uploader-supported-formats', {
                    formats: formatAcceptedFileFormats(acceptedFileFormats),
                    maxSize: maxFileSizeMB,
                })}
            </Text>
            {oversizedFiles.length > 0 && (
                <Alert type={'warning'} size={'large'}>
                    {t('document-uploader-oversized-files', {
                        fileNames: oversizedFiles.join(', '),
                        maxSize: maxFileSizeMB,
                    })}
                </Alert>
            )}
            {files.length > 0 && (
                <Column>
                    <Table headerRows={1} spacing={'small'}>
                        <TableRow>
                            <TableCell>{t('document-uploader-file-name-header')}</TableCell>
                            <TableCell>{t('document-uploader-size-header')}</TableCell>
                            <TableCell>{''}</TableCell>
                        </TableRow>
                        {files.map((fileItem) => {
                            const { status } = fileItem;
                            return (
                                <TableRow key={fileItem.file.name}>
                                    <TableCell>{truncateWithPopover(fileItem.file.name)}</TableCell>
                                    <TableCell>
                                        <Text type={'b200'}>{formatFileSize(fileItem.file.size)}</Text>
                                    </TableCell>
                                    <TableCell alignmentHorizontal={'center'}>
                                        {status === UploadStatus.UPLOADING && (
                                            <Button type={'icon'} disabled={true}>
                                                <Loader size={'small'} />
                                            </Button>
                                        )}
                                        {status === UploadStatus.SUCCESS && (
                                            <Button type={'icon'} disabled={true}>
                                                <SuccessIcon tokens={checkCircleFilledTokens} />
                                            </Button>
                                        )}
                                        {status === UploadStatus.FAILED && (
                                            <Button type={'icon'} onClick={() => removePendingFile(fileItem.file.name)}>
                                                <FailureIcon tokens={closeCircleTokens} />
                                            </Button>
                                        )}
                                        {!status && !isUploading && (
                                            <Button type={'icon'} onClick={() => removePendingFile(fileItem.file.name)}>
                                                <Icon tokens={trashTokens} />
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </Table>
                    {failedFiles.length > 0 && (
                        <Alert type={'error'} size={'large'}>
                            {t('document-uploader-upload-failed', { count: failedFiles.length })}
                        </Alert>
                    )}
                    <Button type={'tertiary'} onClick={handleBatchUpload} disabled={isUploading}>
                        {tCommon('upload-text')}
                    </Button>
                </Column>
            )}
        </>
    );
};
